<script lang="ts">
    export let title: string;
    export let onClose: () => void;
    /** When false, render inline instead of portaling to body. Useful when
     *  the modal is already inside another dialog (e.g. StatusChangeModal). */
    export let usePortal: boolean = true;

    let mouseDownOnBackdrop = false;

    export let titleBackgroundColor: string = 'bg-card';

    // Portal action - teleports element to document body
    function portal(node: HTMLElement, enabled: boolean) {
        if (!enabled) return { destroy() {} };

        const target = document.body;
        target.appendChild(node);

        return {
            destroy() {
                if (node.parentNode) {
                    node.parentNode.removeChild(node);
                }
            }
        };
    }

    function handleMouseDown(e: MouseEvent) {
        mouseDownOnBackdrop = e.target === e.currentTarget;
    }

    function handleMouseUp(e: MouseEvent) {
        if (mouseDownOnBackdrop && e.target === e.currentTarget) {
            onClose();
        }
        mouseDownOnBackdrop = false;
    }

    function handleKeyDown(e: KeyboardEvent) {
        if (e.key === 'Escape') onClose();
    }
</script>

<div
    use:portal={usePortal}
    role="dialog"
    aria-modal="true"
    class=" fixed inset-0 flex items-center justify-center  bg-black/70 z-50 "
    on:mousedown={handleMouseDown}
    on:mouseup={handleMouseUp}
    on:keydown={handleKeyDown}
    tabindex="0"
    data-dont-unselect="true"
>
    <div class="bg-card flex flex-col rounded-lg shadow-lg w-full max-w-md sm:max-w-lg md:max-w-2xl lg:max-w-3xl xl:max-w-4xl max-h-[90vh] m-8 sm:m-4 md:m-0">
        {#if title}
            <div class="flex items-center justify-between rounded-t-lg p-4 border-b {titleBackgroundColor}">
                <h2 class="text-lg font-bold flex-1 text-center">{title}</h2>
                {#if $$slots.headerActions}
                    <div class="flex items-center gap-2">
                        <slot name="headerActions"></slot>
                    </div>
                {/if}
            </div>
        {/if}
        <div class="overflow-y-auto p-4 flex-1">
            <slot></slot>
        </div>
    </div>
</div>

<style lang="scss">
    .new {
        background: hsl(var(--status-new) / 0.3);
        border-bottom-color: hsl(var(--status-new) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-new) / 0.3);
        }
    }

    .needs-survey {
        background: hsl(var(--status-needs-survey) / 0.4);
        border-bottom-color: hsl(var(--status-needs-survey) / 0.7);

        &::after {
            border-left-color: hsl(var(--status-needs-survey) / 0.4);
        }
    }

    .needs-pricing {
        background: hsl(var(--status-needs-pricing) / 0.3);
        border-bottom-color: hsl(var(--status-needs-pricing) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-needs-pricing) / 0.3);
        }
    }

    .tendered {
        background: hsl(var(--status-tendered) / 0.3);
        border-bottom-color: hsl(var(--status-tendered) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-tendered) / 0.3);
        }
    }

    .closed-won {
        background: hsl(var(--status-closed-won) / 0.35);
        border-bottom-color: hsl(var(--status-closed-won) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-closed-won) / 0.35);
        }
    }

    .closed-lost {
        background: hsl(var(--status-closed-lost) / 0.35);
        border-bottom-color: hsl(var(--status-closed-lost) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-closed-lost) / 0.35);
        }
    }

    .booked {
        background: hsl(var(--status-booked) / 0.35);
        border-bottom-color: hsl(var(--status-booked) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-booked) / 0.35);
        }
    }

    .invoiced {
        background: hsl(var(--status-invoiced) / 0.35);
        border-bottom-color: hsl(var(--status-invoiced) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-invoiced) / 0.35);
        }
    }

    .extra-hire {
        background: hsl(var(--status-extra-hire) / 0.35);
        border-bottom-color: hsl(var(--status-extra-hire) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-extra-hire) / 0.35);
        }
    }

    .awaiting-takedown {
        background: hsl(var(--status-awaiting-takedown) / 0.35);
        border-bottom-color: hsl(var(--status-awaiting-takedown) / 0.6);

        &::after {
            border-left-color: hsl(var(--status-awaiting-takedown) / 0.35);
        }
    }
</style>
